import React, { useEffect, useState, useMemo } from 'react';
import {
  getMileageLogs,
  createMileageLog,
  updateMileageLog,
  deleteMileageLog,
  exportMileageCSV,
} from '../services/mileageService';
import { getProjects } from '../services/projectService';
import './MileagePage.css';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export default function MileagePage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filterMode, setFilterMode] = useState('year');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [exporting, setExporting] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingLog, setEditingLog] = useState(null); // log object when editing, null when adding
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    purpose: '',
    startLocation: '',
    endLocation: '',
    startOdometer: '',
    endOdometer: '',
    vehicle: '',
    projectId: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Auto-calc km preview
  const kmPreview = useMemo(() => {
    const start = parseFloat(form.startOdometer);
    const end = parseFloat(form.endOdometer);
    if (!isNaN(start) && !isNaN(end) && end >= start) return (end - start).toFixed(1);
    return null;
  }, [form.startOdometer, form.endOdometer]);

  useEffect(() => {
    getProjects()
      .then((res) => setProjects(res.data.projects || []))
      .catch((err) => console.error('Failed to load projects:', err));
  }, []);

  useEffect(() => {
    loadLogs();
  }, [filterYear, filterMonth, filterMode]); // eslint-disable-line

  const loadLogs = () => {
    setLoading(true);
    const params = { year: filterYear };
    if (filterMode === 'month') params.month = filterMonth;
    getMileageLogs(params)
      .then((res) => setLogs(res.data.logs || []))
      .catch((err) => console.error('Failed to load logs:', err))
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().split('T')[0],
      purpose: '',
      startLocation: '',
      endLocation: '',
      startOdometer: '',
      endOdometer: '',
      vehicle: '',
      projectId: '',
      notes: '',
    });
    setFormError('');
    setEditingLog(null);
  };

  const openAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (log) => {
    setEditingLog(log);
    setForm({
      date: new Date(log.date).toISOString().split('T')[0],
      purpose: log.purpose || '',
      startLocation: log.startLocation || '',
      endLocation: log.endLocation || '',
      startOdometer: String(log.startOdometer),
      endOdometer: String(log.endOdometer),
      vehicle: log.vehicle || '',
      projectId: log.projectId?._id || log.projectId || '',
      notes: log.notes || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.purpose.trim()) { setFormError('Purpose is required.'); return; }
    const start = parseFloat(form.startOdometer);
    const end = parseFloat(form.endOdometer);
    if (isNaN(start) || isNaN(end)) { setFormError('Odometer readings must be numbers.'); return; }
    if (end < start) { setFormError('End odometer must be ≥ start odometer.'); return; }

    setSaving(true);
    setFormError('');
    const payload = {
      date: form.date,
      purpose: form.purpose.trim(),
      startLocation: form.startLocation.trim(),
      endLocation: form.endLocation.trim(),
      startOdometer: start,
      endOdometer: end,
      vehicle: form.vehicle.trim(),
      projectId: form.projectId || null,
      notes: form.notes.trim(),
    };

    try {
      if (editingLog) {
        const res = await updateMileageLog(editingLog._id, payload);
        setLogs((prev) => prev.map((l) => (l._id === editingLog._id ? res.data.log : l)));
      } else {
        const res = await createMileageLog(payload);
        setLogs((prev) => [res.data.log, ...prev]);
      }
      setShowForm(false);
      resetForm();
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Failed to save log.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm('Delete this mileage entry?')) return;
    try {
      await deleteMileageLog(logId);
      setLogs((prev) => prev.filter((l) => l._id !== logId));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete log');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportMileageCSV({
        year: filterYear,
        month: filterMode === 'month' ? filterMonth : undefined,
      });
    } catch (err) {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Group by month
  const logsByMonth = useMemo(() => {
    const grouped = {};
    [...logs].sort((a, b) => new Date(b.date) - new Date(a.date)).forEach((log) => {
      const d = new Date(log.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
      if (!grouped[key]) grouped[key] = { label, logs: [], totalKm: 0 };
      grouped[key].logs.push(log);
      grouped[key].totalKm += log.kmDriven || 0;
    });
    return Object.values(grouped);
  }, [logs]);

  const yearlyTotal = useMemo(() => logs.reduce((sum, l) => sum + (l.kmDriven || 0), 0), [logs]);

  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) yearOptions.push(y);

  return (
    <div className="mileage-page">
      <div className="mileage-header">
        <div className="mileage-header-left">
          <h1 className="mileage-title">🚗 Mileage Log</h1>
          <p className="mileage-subtitle">CRA T2200/T777 — Keep this log for employment expense claims.</p>
        </div>
        <div className="mileage-header-controls">
          <select
            className="mileage-year-select"
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
          >
            <option value="year">Total Year</option>
            <option value="month">Single Month</option>
          </select>
          <select
            className="mileage-year-select"
            value={filterYear}
            onChange={(e) => setFilterYear(Number(e.target.value))}
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {filterMode === 'month' && (
            <select
              className="mileage-year-select"
              value={filterMonth}
              onChange={(e) => setFilterMonth(Number(e.target.value))}
            >
              {MONTH_OPTIONS.map((month) => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          )}
          <button className="btn btn-ghost btn-sm" onClick={handleExport} disabled={exporting}>
            {exporting
              ? 'Exporting…'
              : filterMode === 'month'
                ? `⬇ Export CSV ${filterYear}-${String(filterMonth).padStart(2, '0')}`
                : `⬇ Export CSV ${filterYear}`}
          </button>
          <button className="btn btn-primary btn-sm" onClick={openAdd}>+ Log Trip</button>
        </div>
      </div>

      {/* Log entry form */}
      {showForm && (
        <div className="mileage-form-card">
          <h3 className="mileage-form-title">{editingLog ? 'Edit Trip' : 'Log a Trip'}</h3>
          <form onSubmit={handleSubmit} className="mileage-form">
            <div className="mileage-form-row">
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input type="date" className="form-input" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Vehicle</label>
                <input className="form-input" placeholder="e.g. 2020 Honda Civic" value={form.vehicle} onChange={(e) => setForm((p) => ({ ...p, vehicle: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Purpose / Business Reason *</label>
              <input className="form-input" placeholder="e.g. Client meeting — Acme Corp" value={form.purpose} onChange={(e) => setForm((p) => ({ ...p, purpose: e.target.value }))} required />
            </div>
            <div className="mileage-form-row">
              <div className="form-group">
                <label className="form-label">From</label>
                <input className="form-input" placeholder="Start location" value={form.startLocation} onChange={(e) => setForm((p) => ({ ...p, startLocation: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">To</label>
                <input className="form-input" placeholder="End location" value={form.endLocation} onChange={(e) => setForm((p) => ({ ...p, endLocation: e.target.value }))} />
              </div>
            </div>
            <div className="mileage-form-row">
              <div className="form-group">
                <label className="form-label">Start Odometer (km) *</label>
                <input type="number" className="form-input" placeholder="e.g. 45000" step="0.1" value={form.startOdometer} onChange={(e) => setForm((p) => ({ ...p, startOdometer: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">End Odometer (km) *</label>
                <input type="number" className="form-input" placeholder="e.g. 45087" step="0.1" value={form.endOdometer} onChange={(e) => setForm((p) => ({ ...p, endOdometer: e.target.value }))} required />
              </div>
              <div className="form-group mileage-calc-display">
                <label className="form-label">Distance</label>
                <div className="mileage-km-preview">{kmPreview !== null ? `${kmPreview} km` : '—'}</div>
              </div>
            </div>
            <div className="mileage-form-row">
              <div className="form-group">
                <label className="form-label">Project</label>
                <select className="form-select" value={form.projectId} onChange={(e) => setForm((p) => ({ ...p, projectId: e.target.value }))}>
                  <option value="">No project</option>
                  {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-input" placeholder="Optional notes" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>

            {formError && <p className="form-error">{formError}</p>}

            <div className="form-row">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : editingLog ? 'Update' : 'Save Trip'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => { setShowForm(false); resetForm(); }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Yearly total */}
      {!loading && (
        <div className="mileage-yearly-total">
          <span className="mileage-total-label">
            {filterMode === 'month'
              ? `${MONTH_OPTIONS.find((month) => month.value === filterMonth)?.label || 'Month'} ${filterYear} Total:`
              : `${filterYear} Total:`}
          </span>
          <span className="mileage-total-value">{yearlyTotal.toFixed(1)} km</span>
        </div>
      )}

      {/* Logs grouped by month */}
      {loading ? (
        <p className="panel-muted">Loading logs…</p>
      ) : logsByMonth.length === 0 ? (
        <p className="panel-muted">
          No trips logged for {filterMode === 'month' ? `${MONTH_OPTIONS.find((month) => month.value === filterMonth)?.label || 'selected month'} ${filterYear}` : filterYear}. Click "+ Log Trip" to add one.
        </p>
      ) : (
        logsByMonth.map((month) => (
          <div key={month.label} className="mileage-month-group">
            <div className="mileage-month-header">
              <span className="mileage-month-label">{month.label}</span>
              <span className="mileage-month-total">{month.totalKm.toFixed(1)} km</span>
            </div>
            <table className="mileage-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Purpose</th>
                  <th>From → To</th>
                  <th>Start odo</th>
                  <th>End odo</th>
                  <th>km</th>
                  <th>Vehicle</th>
                  <th>Project</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {month.logs.map((log) => (
                  <tr key={log._id}>
                    <td data-label="Date">{new Date(log.date).toLocaleDateString('en-CA')}</td>
                    <td data-label="Purpose">{log.purpose}</td>
                    <td data-label="From → To">{[log.startLocation, log.endLocation].filter(Boolean).join(' → ') || '—'}</td>
                    <td data-label="Start odo">{log.startOdometer?.toLocaleString()}</td>
                    <td data-label="End odo">{log.endOdometer?.toLocaleString()}</td>
                    <td data-label="KM" className="mileage-km-cell">{log.kmDriven?.toFixed(1)}</td>
                    <td data-label="Vehicle">{log.vehicle || '—'}</td>
                    <td data-label="Project">{log.projectId?.name || '—'}</td>
                    <td data-label="Actions" className="mileage-actions">
                      <button className="btn-icon" title="Edit" onClick={() => openEdit(log)}>✏</button>
                      <button className="btn-icon-danger" title="Delete" onClick={() => handleDelete(log._id)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      <p className="mileage-cra-note">
        📋 CRA requirement: Record date, purpose, starting and ending locations, odometer readings, and business reason for each trip. Retain vehicle logbook and supporting receipts.
      </p>
    </div>
  );
}
